import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useEffect, useState } from "react";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { toast } from "sonner";
import { alertsRepo } from "@/lib/repositories";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useTranslation } from "react-i18next";
import { queryClient } from "@/lib/query-client";
import DotPattern from "@/components/dot-pattern";
import { Skeleton } from "@/components/ui/skeleton";
import AlertForm from "@/features/alerts/components/forms/alert-form";
import { alertFormSchema } from "@/features/alerts/components/forms/schemas";
import { useFetchAlert } from "@/features/alerts/hooks/useFetchAlert";
import { useDynamicPageTitle } from "@/hooks/useDynamicPageTitle";
import type { ApiNotificationRule } from "@/features/alerts/types";

type AlertEditFormContentProps = { alert: ApiNotificationRule };

const AlertEditFormContent = ({ alert }: AlertEditFormContentProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof alertFormSchema>>({
    resolver: zodResolver(alertFormSchema),
    defaultValues: {
      frequency: alert.frequency,
      send_email: alert.send_email,
      alert_type: alert.alert_type,
      dilemma: alert.dilemma ?? undefined,
      title: alert.title,
      message: alert.message,
      is_active: alert.is_active,
    },
  });

  useEffect(() => {
    form.reset({
      frequency: alert.frequency,
      send_email: alert.send_email,
      alert_type: alert.alert_type,
      dilemma: alert.dilemma ?? undefined,
      title: alert.title,
      message: alert.message,
      is_active: alert.is_active,
    });
  }, [alert, form]);

  async function onSubmit(data: z.infer<typeof alertFormSchema>) {
    if (!id) return;
    setIsSubmitting(true);
    const payload = {
      ...data,
      send_push: true,
      dilemma: data.alert_type === "focus_dilemma" ? data.dilemma : undefined,
    };
    const [error] = await catchError(alertsRepo.patch(id, payload));
    if (error) {
      toast.error(handleApiError(error).message);
      setIsSubmitting(false);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["alerts"] });
    toast.success(t("messages.alert_edit_success"));
    navigate(ROUTES.WARNINGS);
    setIsSubmitting(false);
  }

  return (
    <AlertForm
      form={form}
      onSubmit={onSubmit}
      isLoading={isSubmitting}
      alert={alert}
    />
  );
};

const AlertEditPage = () => {
  const { id } = useParams();
  const { alert, isLoading } = useFetchAlert(id);
  useDynamicPageTitle(alert ? alert.title : "");

  return (
    <div className="relative h-full flex flex-col items-center gap-y-3 p-3 py-4 sm:py-10 sm:p-6">
      <DotPattern />
      {isLoading && <Skeleton className="w-full h-137 md:h-80" />}
      {alert && <AlertEditFormContent alert={alert} />}
    </div>
  );
};

export default AlertEditPage;
